import http from './httpClient';

class UnitMappingService {
  // Get business ID from current auth context first to avoid stale cross-login values.
  static getBusinessId() {
    try {
      if (typeof window !== 'undefined') {
        // 1) Prefer current user object saved at login
        const userStr = window.localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            const fromUser = Number(user?.businessId ?? user?.business_id);
            if (!Number.isNaN(fromUser) && fromUser > 0) return fromUser;
          } catch (_) {}
        }

        // 2) Try explicit localStorage value as secondary source
        const ls = window.localStorage.getItem('businessId');
        const fromLs = Number(ls);
        if (!Number.isNaN(fromLs) && fromLs > 0) return fromLs;

        // 3) Try to derive from JWT token (stored as 'token' in this app)
        const token = window.localStorage.getItem('token') || window.localStorage.getItem('authToken');
        if (token && token.split('.').length === 3) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const fromJwt = Number(payload?.businessId ?? payload?.business_id);
            if (!Number.isNaN(fromJwt) && fromJwt > 0) return fromJwt;
          } catch (_) {
            // ignore decode errors
          }
        }
      }
    } catch (_) {
      // ignore env without window/localStorage
    }
    // 4) No valid business found
    return null;
  }

  // Get all available units categorized by type
  static async getUnitOptions() {
    try {
  const data = await http.get('/unit-mapping/units');
  if (!data.success) throw new Error(data.error || 'Failed to fetch unit options');
  return data.data;
    } catch (error) {
      console.error('Error fetching unit options:', error);
      throw error;
    }
  }

  // Get kitchen units for a business
  static async getKitchenUnits(businessId) {
    try {
  const data = await http.get(`/unit-mapping/kitchen-units/${businessId}`);
  if (!data.success) throw new Error(data.error || 'Failed to fetch kitchen units');
  return data.data;
    } catch (error) {
      console.error('Error fetching kitchen units:', error);
      throw error;
    }
  }

  // Save kitchen units for a business
  static async saveKitchenUnits(businessId, units) {
    try {
  return await http.post(`/unit-mapping/kitchen-units/${businessId}`, { units });
    } catch (error) {
      console.error('Error saving kitchen units:', error);
      throw error;
    }
  }

  // Save a single kitchen unit (conversion)
  static async saveKitchenUnit(data) {
    try {
      const { businessId, item, kitchenTool, quantity, unit, type = 'kitchen' } = data;
      // Create a single conversion object to send to the backend
      const conversion = {
        item: item,
        kitchenTool: kitchenTool,
        quantity: Number(quantity),
        unit: unit
      };
      return await http.post(`/unit-mapping/kitchen-units/${businessId}`, { 
        conversions: [conversion],
        type: type  // Pass conversion type
      });
    } catch (error) {
      console.error('Error saving kitchen unit:', error);
      throw error;
    }
  }

  // Get ingredient-scoped kitchen conversions
  static async getIngredientKitchenUnits(businessId, type = 'kitchen') {
    try {
      const data = await http.get(`/unit-mapping/kitchen-units/${businessId}/by-ingredient?type=${type}`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch ingredient kitchen units');
      return data.data;
    } catch (error) {
      console.error('Error fetching ingredient kitchen units:', error);
      throw error;
    }
  }

  // Save ingredient-scoped kitchen conversions
  static async saveIngredientKitchenUnits(businessId, conversions) {
    try {
      return await http.post(`/unit-mapping/kitchen-units/${businessId}`, { conversions });
    } catch (error) {
      console.error('Error saving ingredient kitchen units:', error);
      throw error;
    }
  }

  // Get inventory items for supplier conversions
  static async getInventoryItems(businessId) {
    try {
  const data = await http.get(`/unit-mapping/inventory-items/${businessId}`);
  if (!data.success) throw new Error(data.error || 'Failed to fetch inventory items');
  return data.data;
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }
  }

  // Get supplier conversions for a business
  static async getSupplierConversions(businessId) {
    try {
  const data = await http.get(`/unit-mapping/supplier-conversions/${businessId}`);
  if (!data.success) throw new Error(data.error || 'Failed to fetch supplier conversions');
  return data.data;
    } catch (error) {
      console.error('Error fetching supplier conversions:', error);
      throw error;
    }
  }

  // Save supplier conversions for a business
  static async saveSupplierConversions(businessId, conversions) {
    try {
  return await http.post(`/unit-mapping/supplier-conversions/${businessId}`, { conversions });
    } catch (error) {
      console.error('Error saving supplier conversions:', error);
      throw error;
    }
  }

  // Save a single supplier conversion (for container units)
  static async saveSupplierConversion(data) {
    try {
      const { businessId, item, containerType, quantity, unit } = data;
      // Create a single conversion object to send to the backend
      const conversion = {
        item: item,
        containerType: containerType,
        quantity: Number(quantity),
        unit: unit
      };
      return await http.post(`/unit-mapping/supplier-conversions/${businessId}`, { 
        conversions: [conversion] 
      });
    } catch (error) {
      console.error('Error saving supplier conversion:', error);
      throw error;
    }
  }

  // Complete the unit mapping setup
  static async completeSetup(businessId) {
    try {
  return await http.post(`/unit-mapping/complete-setup/${businessId}`);
    } catch (error) {
      console.error('Error completing setup:', error);
      throw error;
    }
  }

  // Get all business unit conversions
  static async getBusinessConversions(businessId) {
    try {
  const data = await http.get(`/unit-mapping/conversions/${businessId}`);
  if (!data.success) throw new Error(data.error || 'Failed to fetch business conversions');
  return data.data;
    } catch (error) {
      console.error('Error fetching business conversions:', error);
      throw error;
    }
  }

  // Lookup unit_id by symbol (direct GlobalUnits query)
  static async lookupUnitBySymbol(unitSymbol) {
    try {
      const data = await http.get(`/unit-mapping/units/by-symbol/${encodeURIComponent(unitSymbol)}`);
      if (!data.success) throw new Error(data.error || 'Unit not found');
      return data.data;
    } catch (error) {
      console.error(`Error looking up unit '${unitSymbol}':`, error);
      throw error;
    }
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 🆕 GLOBAL UNIT STANDARDIZATION - New Methods
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /**
   * Get unit information for an item (unit family, allowed units, usage stats)
   * @param {number} itemId - Item ID
   * @returns {Promise<object>} - Unit info including family, allowed units, usage
   */
  static async getItemUnitInfo(itemId) {
    try {
      const data = await http.get(`/inventory/items/${itemId}/unit-info`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch unit info');
      return data.data;
    } catch (error) {
      console.error('Error fetching item unit info:', error);
      throw error;
    }
  }

  /**
   * Preview unit change impact (what recipes, batches, preparations will be affected)
   * @param {string} itemName - Item name
   * @param {string} currentUnit - Current unit symbol
   * @param {string} newUnit - New unit symbol
   * @param {number|null} conversionRate - Conversion factor (required for cross-family)
   * @returns {Promise<object>} - Preview data with affected entities
   */
  static async previewUnitChange(itemName, currentUnit, newUnit, conversionRate = null) {
    try {
      const payload = {
        itemName,
        currentUnit,
        newUnit,
        preview: true
      };

      if (conversionRate !== null && conversionRate !== undefined) {
        payload.conversionRate = conversionRate;
      }

      const data = await http.put('/inventory/items/change-unit', payload);
      if (!data.success) throw new Error(data.error || 'Failed to preview unit change');
      return data;
    } catch (error) {
      console.error('Error previewing unit change:', error);
      throw error;
    }
  }

  /**
   * Confirm and apply unit change
   * @param {string} itemName - Item name
   * @param {string} currentUnit - Current unit symbol
   * @param {string} newUnit - New unit symbol
   * @param {number} conversionRate - Conversion factor
   * @param {string|null} reason - Optional reason for the change
   * @returns {Promise<object>} - Result of unit change
   */
  static async confirmUnitChange(itemName, currentUnit, newUnit, conversionRate, reason = null) {
    try {
      const payload = {
        itemName,
        currentUnit,
        newUnit,
        conversionRate,
        preview: false
      };

      if (reason) {
        payload.reason = reason;
      }

      const data = await http.put('/inventory/items/change-unit', payload);
      if (!data.success) throw new Error(data.error || 'Failed to change unit');
      return data;
    } catch (error) {
      console.error('Error changing unit:', error);
      throw error;
    }
  }

  /**
   * Get unit family for a given unit symbol
   * @param {string} symbol - Unit symbol (e.g., 'g', 'ml', 'pcs')
   * @returns {string|null} - 'weight', 'volume', 'count', or null
   */
  static getUnitFamily(symbol) {
    if (!symbol) return null;
    const normalized = String(symbol).toLowerCase().trim();

    const weightUnits = ['kg', 'g', 'mg', 'kilogram', 'kilograms', 'gram', 'grams', 'milligram', 'milligrams'];
    if (weightUnits.includes(normalized)) return 'weight';

    const volumeUnits = ['l', 'ml', 'liter', 'litre', 'liters', 'litres', 'milliliter', 'millilitre', 'milliliters', 'millilitres'];
    if (volumeUnits.includes(normalized)) return 'volume';

    const countUnits = ['pcs', 'piece', 'pieces', 'nos', 'number', 'numbers', 'unit', 'units', 'pc'];
    if (countUnits.includes(normalized)) return 'count';

    return null;
  }

  /**
   * Check if a unit is a kitchen tool shortcut
   * @param {string} symbol - Unit symbol
   * @returns {boolean} - True if kitchen tool, false otherwise
   */
  static isKitchenToolUnit(symbol) {
    if (!symbol) return false;
    const normalized = String(symbol).toLowerCase().trim();
    const kitchenTools = ['cup', 'tbsp', 'tsp', 'tablespoon', 'teaspoon', 'pinch', 'bowl', 'glass'];
    return kitchenTools.includes(normalized);
  }

  /**
   * Check if a unit is a container shortcut
   * @param {string} symbol - Unit symbol
   * @returns {boolean} - True if container unit, false otherwise
   */
  static isContainerUnit(symbol) {
    if (!symbol) return false;
    const normalized = String(symbol).toLowerCase().trim();
    const containers = ['box', 'boxes', 'carton', 'cartons', 'bag', 'bags', 'bottle', 'bottles', 'pack', 'packs', 'packet', 'packets', 'can', 'cans', 'jar', 'jars'];
    return containers.includes(normalized);
  }

  /**
   * Check if a unit is one of the 5 allowed base units
   * @param {string} symbol - Unit symbol
   * @returns {boolean} - True if base unit, false otherwise
   */
  static isBaseUnit(symbol) {
    if (!symbol) return false;
    const normalized = String(symbol).toLowerCase().trim();
    const baseUnits = ['kg', 'g', 'l', 'ml', 'pcs', 'piece', 'nos'];
    return baseUnits.includes(normalized);
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 🆕 UNIT CONTRACT helpers (plan §4.0 / Bug 10) — units are explicit DATA,
  // never inferred from a value's magnitude. Canonical base units: g, ml, pcs.
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /**
   * The canonical base unit for a given unit symbol.
   * @param {string} symbol
   * @returns {'g'|'ml'|'pcs'|string} base unit ('g' | 'ml' | 'pcs'), or the
   *   original symbol for unknown/custom units (treated 1:1 with itself).
   */
  static baseUnitOf(symbol) {
    const s = String(symbol || '').toLowerCase().trim();
    if (['kg', 'kilogram', 'kilograms', 'g', 'gram', 'grams', 'gm', 'mg', 'milligram', 'milligrams', 'ton', 'tonne'].includes(s)) return 'g';
    if (['l', 'ltr', 'liter', 'litre', 'liters', 'litres', 'ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres'].includes(s)) return 'ml';
    if (['pcs', 'pc', 'piece', 'pieces', 'nos', 'no', 'unit', 'units', 'each', 'ea'].includes(s)) return 'pcs';
    return s || '';
  }

  /**
   * Multiplier to convert a quantity in `symbol` into its base unit (g/ml/pcs).
   * Unknown units default to 1 (treated as their own base). No magnitude guessing.
   * @param {string} symbol
   * @returns {number}
   */
  static factorToBase(symbol) {
    const s = String(symbol || '').toLowerCase().trim();
    const map = {
      kg: 1000, kilogram: 1000, kilograms: 1000,
      g: 1, gram: 1, grams: 1, gm: 1,
      mg: 0.001, milligram: 0.001, milligrams: 0.001,
      ton: 1000000, tonne: 1000000,
      l: 1000, ltr: 1000, liter: 1000, litre: 1000, liters: 1000, litres: 1000,
      ml: 1, milliliter: 1, millilitre: 1, milliliters: 1, millilitres: 1,
    };
    return map[s] ?? 1;
  }

  /**
   * Compute a monetary value from a matched (quantity, unit, cost) triple.
   * `costPerStdUnit` is the price per the item's own standard unit (e.g. per kg).
   * The unit is REQUIRED and explicit — never inferred from cost magnitude (Bug 10).
   * @param {number} qtyBase - quantity already in base units (g/ml/pcs)
   * @param {string} unitSymbol - the item's standard unit symbol
   * @param {number} costPerStdUnit - cost per standard unit
   * @returns {number}
   */
  static valueOf(qtyBase, unitSymbol, costPerStdUnit) {
    const qty = Number(qtyBase);
    const cost = Number(costPerStdUnit);
    if (!Number.isFinite(qty) || !Number.isFinite(cost) || qty <= 0 || cost <= 0) return 0;
    const factor = UnitMappingService.factorToBase(unitSymbol); // base units per std unit
    const costPerBase = factor > 0 ? cost / factor : cost;      // e.g. ₹/kg ÷ 1000 = ₹/g
    return qty * costPerBase;
  }

  /**
   * Normalize any unit string to one of the 5 standard display symbols: g, kg, ml, L, pcs
   * This is the SINGLE SOURCE OF TRUTH for unit display across the app.
   * Accepts long-form names, abbreviations, plurals, etc. and always returns the standard short symbol.
   * Returns the original string if no match is found (for custom/kitchen units).
   * @param {string} unit - Any unit string
   * @returns {string} - Normalized symbol: 'g', 'kg', 'ml', 'L', or 'pcs' (or original if unknown)
   */
  static normalizeUnitSymbol(unit) {
    if (!unit) return '';
    const lower = String(unit).toLowerCase().trim();
    if (!lower) return '';

    // Weight — grams
    if (['g', 'gm', 'gms', 'gram', 'grams', 'grams (g)', 'grams(g)'].includes(lower)) return 'g';
    // Weight — kilograms
    if (['kg', 'kgs', 'kilogram', 'kilograms', 'kilograms (kg)', 'kilograms(kg)'].includes(lower)) return 'kg';
    // Weight — milligrams (display as mg but rarely used as base)
    if (['mg', 'mgs', 'milligram', 'milligrams'].includes(lower)) return 'mg';

    // Volume — milliliters
    if (['ml', 'mls', 'milliliter', 'milliliters', 'millilitre', 'millilitres'].includes(lower)) return 'ml';
    // Volume — liters
    if (['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'].includes(lower)) return 'L';

    // Count — pieces
    if (['pcs', 'pc', 'piece', 'pieces', 'nos', 'number', 'numbers', 'unit', 'units'].includes(lower)) return 'pcs';

    // Return original (trimmed) for custom/kitchen/container units like 'cup', 'tbsp', 'box', etc.
    return unit.trim();
  }
}

export default UnitMappingService;